import React, { createContext, useContext, useState, useEffect } from 'react';
import { isSessionTokenUsable, SESSION_NOTICE_KEY } from './session-token';

console.log('[auth-context] Module loading...');

interface User {
  id: string;
  name: string;
  role: string;
  permissions: string[];
  tenantId?: string;       // Tenant/Firma ID
  tenantCurrency?: string; // TRY, EUR, USD
  tenantCountry?: string;  // TR, DE, US, etc.
  tenantPlan?: string;     // starter, professional, enterprise
  branchId?: string | null;   // Atanmış şube ID (null = tüm şubeler, patronlar için)
  branchName?: string | null; // Şube adı (display için)
}

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  testCurrency: string | null; // Test için geçici currency override
  setTestCurrency: (currency: string | null) => void;
  isLoading: boolean; // Loading state ekledik
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

console.log('[auth-context] AuthContext created successfully');

export function AuthProvider({ children }: { children: React.ReactNode }) {
  console.log('[AuthProvider] Rendering...');
  const [user, setUser] = useState<User | null>(null);
  const [testCurrency, setTestCurrencyState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Loading state

  useEffect(() => {
    // Check localStorage for existing session
    const savedUser = localStorage.getItem('user');
    const savedTenantId = localStorage.getItem('tenantId');
    
    console.log('[AuthProvider] Checking saved session:', { 
      hasUser: !!savedUser, 
      hasTenantId: !!savedTenantId 
    });
    
    if (savedUser && savedTenantId && !isSessionTokenUsable(localStorage.getItem('sessionToken'))) {
      // Oturum belgesi yok ya da süresi dolmuş: oturum açık tutulmaz, bir kez yeniden giriş istenir
      console.log('[AuthProvider] Session token missing/expired — relogin required');
      localStorage.removeItem('user');
      localStorage.removeItem('sessionToken');
      localStorage.setItem(SESSION_NOTICE_KEY, '1');
    } else if (savedUser && savedTenantId) {
      const userData = JSON.parse(savedUser);
      console.log('[AuthProvider] Restoring user session:', userData.name);
      setUser(userData);
    } else {
      console.log('[AuthProvider] No saved session found');
    }
    
    // Load test currency from localStorage
    const savedTestCurrency = localStorage.getItem('testCurrency');
    if (savedTestCurrency) {
      setTestCurrencyState(savedTestCurrency);
    }
    
    // Auth restore tamamlandı
    setIsLoading(false);
  }, []);

  const login = (userData: User) => {
    console.log('[AuthProvider] Login called with user:', userData.name);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    console.log('[AuthProvider] User saved to localStorage');
  };

  const logout = () => {
    console.log('[AuthProvider] Logout called - clearing session data');
    
    // Clear user state
    setUser(null);
    
    // Clear user session data from localStorage (keep tenant ID for convenience)
    localStorage.removeItem('user');
    localStorage.removeItem('hasLoggedInBefore');
    localStorage.removeItem('branchId');
    localStorage.removeItem('sessionToken');
    
    // Keep tenantId and language preference
    const savedLanguage = localStorage.getItem('appLanguage');
    
    console.log('[AuthProvider] Session cleared. Tenant and language preserved:', { savedLanguage });
  };

  const hasPermission = (permission: string) => {
    if (!user) return false;
    if (user.permissions.includes('all')) return true;
    return user.permissions.includes(permission);
  };
  
  const setTestCurrency = (currency: string | null) => {
    setTestCurrencyState(currency);
    if (currency) {
      localStorage.setItem('testCurrency', currency);
    } else {
      localStorage.removeItem('testCurrency');
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission, testCurrency, setTestCurrency, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    // Development fallback - cache sorunu için
    console.warn('[auth] useAuth called outside AuthProvider - returning fallback');
    return {
      user: null,
      login: () => {},
      logout: () => {},
      hasPermission: () => false,
      testCurrency: null,
      setTestCurrency: () => {},
      isLoading: false,
    };
  }
  return context;
}